
export interface ExtractedColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

export const extractColorsFromImage = async (imageUrl: string): Promise<ExtractedColors> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Set canvas size to image size
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Extract colors using color quantization
        const colorMap = new Map<string, number>();
        
        // Sample every 4th pixel for performance
        for (let i = 0; i < data.length; i += 16) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Skip transparent pixels
          if (a < 128) continue;
          
          // Convert to hex
          const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
          
          colorMap.set(hex, (colorMap.get(hex) || 0) + 1);
        }
        
        // Sort colors by frequency
        const sortedColors = Array.from(colorMap.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([color]) => color);
        
        // Filter out very light and very dark colors for better palette
        const filteredColors = sortedColors.filter(color => {
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          return brightness > 30 && brightness < 225; // Avoid pure black/white
        });
        
        // Generate color palette
        const colors: ExtractedColors = {
          primary: filteredColors[0] || '#3B82F6',
          secondary: filteredColors[1] || '#1E40AF',
          accent: filteredColors[2] || '#F59E0B',
          background: '#FFFFFF',
          text: '#1F2937'
        };
        
        // Determine if we need dark or light theme based on primary color
        const primaryColor = colors.primary;
        const r = parseInt(primaryColor.slice(1, 3), 16);
        const g = parseInt(primaryColor.slice(3, 5), 16);
        const b = parseInt(primaryColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        
        if (brightness < 128) {
          // Dark primary color - use light background
          colors.background = '#FFFFFF';
          colors.text = '#1F2937';
        } else {
          // Light primary color - consider darker background
          colors.background = '#F9FAFB';
          colors.text = '#111827';
        }
        
        resolve(colors);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};
